/**
 * Where a person goes once a session exists.
 *
 * Three things want a say and they are not equally trustworthy: a file the
 * visitor picked before they had an account, a `?next=` in the address bar,
 * and the route `ProtectedRoute` bounced them off. The file wins — it is the
 * only one the person actually did something to create — and `?next=` is
 * attacker-controlled, so it is sanitised the way the gateway sanitises its
 * own `returnTo`.
 */

/** Where the funnel resumes: the step that uploads the held file. */
export const FUNNEL_RESUME = '/onboarding/analyse'

/**
 * The trial funnel's own path prefix, named once because two things key on
 * it: where a person is sent back to after signing in, and — more
 * importantly — *which* auth screen a signed-out visitor is sent to in the
 * first place. See `authScreenFor`.
 */
export const FUNNEL_PREFIX = '/onboarding'

/**
 * Which of the two auth screens a signed-out visitor should meet.
 *
 * Someone who has just picked a form in the trial funnel has no account —
 * that is the entire premise of the funnel, and the marketing site sends them
 * here promising "Formular wählen → Konto anlegen". Sending them to
 * "Willkommen zurück" asks them to remember a password they never set, and
 * the way on from there is a link they have to notice. Everywhere else,
 * signing in is the better guess.
 */
export function authScreenFor(pathname: string): '/login' | '/register' {
  return pathname.startsWith(FUNNEL_PREFIX) ? '/register' : '/login'
}

/**
 * A redirect target that cannot leave this origin.
 *
 * Mirrors `sanitizeReturnTo` in the gateway (`internal/api/oauth_handlers.go`):
 * one leading slash, never two (`//evil.example` is a protocol-relative URL),
 * no backslash (IE and some parsers read `/\evil.example` as one), and no
 * CR/LF, which would split a header on the way through.
 */
export function safeReturnTo(raw: string | null | undefined): string | null {
  if (!raw) return null
  if (!raw.startsWith('/')) return null
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null
  if (/[\r\n\t]/.test(raw)) return null
  return raw
}

export interface AuthDestinationInput {
  /** A file is waiting in IndexedDB from the pre-account funnel. */
  pendingUpload: boolean
  /** The `?next=` parameter, unsanitised. */
  next?: string | null
  /** Where `ProtectedRoute` bounced them off, via router state. */
  from?: string | null
}

/** Where to land after a successful sign-in. */
export function destinationAfterSignIn({ pendingUpload, next, from }: AuthDestinationInput): string {
  if (pendingUpload) return FUNNEL_RESUME
  return safeReturnTo(next) ?? safeReturnTo(from) ?? '/'
}

/**
 * Where to land after a successful registration.
 *
 * Registration now returns a session (the gateway answers 201 with a token
 * pair and sets the refresh cookie; confirmation is enforced later, by the
 * grace window in `internal/api/verification.go`), so someone who arrived
 * from the funnel goes straight on to their form. That is the whole promise
 * of "Danach geht es direkt mit deinem Formular weiter."
 *
 * Someone who registered without a form in hand has nowhere urgent to be, so
 * they get the confirmation screen — the one moment where "look in your
 * inbox" is the most useful thing on screen.
 */
export function destinationAfterRegister(input: AuthDestinationInput): string {
  if (input.pendingUpload || safeReturnTo(input.next)) return destinationAfterSignIn(input)
  return '/verify-email'
}

/** True when the visitor came through the funnel, which is what decides
 *  whether the register page shows its "So geht's weiter" column. */
export function arrivedFromFunnel(input: AuthDestinationInput): boolean {
  return input.pendingUpload || safeReturnTo(input.next) != null
}
